import React, { useContext, useEffect, useState } from 'react';
import { FormContext, FormSchemaFields } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { FormModal } from '../../Shared/form';
import { ProjectContext } from '../../../contexts/ProjectContext';
import AppContext from '../../../contexts/AppContext';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import { toast } from 'react-toastify';
import billingAddonApi, { BillingAddon, BillingAddonFormAssignment } from '../../../utils/api/billingAddon';

interface Template {
  template_id: number;
  template_name: string;
  inspection_types: string;
  is_primary: boolean;
}

interface Interval {
  interval_id: number;
  interval_name: string;
}

interface Inspector {
  user_id: number;
  user_name: string;
  user_email: string;
}

interface RoutinesApiResponse {
  success: boolean;
  data: {
    available_templates: Template[];
    available_intervals: Interval[];
    available_inspectors: Inspector[];
    current_associations: any[];
  };
  message: string;
}

interface InspectionModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isEdit: boolean;
  initialData?: any;
  projectId?: number;
  divisionId?: number;
  existingInspectionTypes?: string[];
  routineId?: number;
  junctionId?: number;
  inspectorOnly?: boolean;
}

const InspectionModal: React.FC<InspectionModalProps> = ({
  show,
  onClose,
  onSubmit,
  isEdit,
  initialData = {},
  projectId,
  divisionId,
  existingInspectionTypes = [],
  routineId,
  junctionId,
  inspectorOnly = false,
}) => {
  const formContext = useContext(FormContext);
  const projectStore = useContext(ProjectContext);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState<RoutinesApiResponse['data'] | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [editTemplateOptions, setEditTemplateOptions] = useState<Template[]>([]);

  const [activeTab, setActiveTab] = useState<'settings' | 'addons'>('settings');
  const [hasDivisionAddons, setHasDivisionAddons] = useState(false);
  const [formAssignments, setFormAssignments] = useState<BillingAddonFormAssignment[]>([]);
  const [availableAddons, setAvailableAddons] = useState<BillingAddon[]>([]);
  const [selectedAddonId, setSelectedAddonId] = useState<number | ''>('');
  const [addonBusy, setAddonBusy] = useState(false);

  // Fetch routines data from API
  const fetchRoutinesData = async () => {
    const currentProjectId = projectId || projectStore.project?.id;
    let currentDivisionId = divisionId;

    // Fallback: try to get division ID from localStorage
    if (!currentDivisionId) {
      const storedDivisionId = localStorage.getItem('divisionId');

      if (storedDivisionId) {
        currentDivisionId = JSON.parse(storedDivisionId);
      }
    }

    console.log('fetchRoutinesData called with:', {
      currentProjectId,
      currentDivisionId,
      projectId,
      divisionId,
      projectStoreProject: projectStore.project,
      storedDivisionId: localStorage.getItem('divisionId')
    });

    if (!currentProjectId) {
      console.log('Missing project ID - not making API call');

      return;
    }

    if (!currentDivisionId) {
      console.log('Missing division ID - not making API call');

      return;
    }

    setIsLoading(true);
    try {
      console.log('Making API call to getProjectRoutines...');
      const response: RoutinesApiResponse = await schedulerApi.getProjectRoutines(currentDivisionId, currentProjectId);

      console.log('API response received:', response);

      if (response.success) {
        setApiData(response.data);
        console.log('API data set successfully:', response.data);
      } else {
        console.error('API call failed:', response);
      }
    } catch (error) {
      console.error('Error fetching routines data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchRoutinesData();
    }
  }, [show, projectId, divisionId, projectStore.project?.id]);

  useEffect(() => {
    if (show && isEdit && routineId) {
      schedulerApi.getRoutineEditOptions(routineId)
        .then((res) => {
          if (res?.success && res.data?.available_templates) {
            setEditTemplateOptions(res.data.available_templates);
          }
        })
        .catch(() => { });
    }
  }, [show, isEdit, routineId]);

  useEffect(() => {
    if (show && apiData) {
      if (isEdit && initialData) {
        // Pre-populate form with existing data when editing
        Object.keys(initialData).forEach(key => {
          formContext.set(key, initialData[key]);
        });

        // Set selected template for editing and populate inspection type.
        // In edit mode the template list comes from getRoutineEditOptions (editTemplateOptions),
        // which for EVP/external projects may contain templates not present in
        // apiData.available_templates. Look in editTemplateOptions first, then fall back.
        if (initialData.inspectionTemplateName) {
          const template =
            editTemplateOptions.find(t => t.template_id.toString() === initialData.inspectionTemplateName) ||
            apiData.available_templates.find(t => t.template_id.toString() === initialData.inspectionTemplateName) ||
            null;

          setSelectedTemplate(template);

          if (template) {
            formContext.set('inspectionTemplateType', template.inspection_types);
          }
        }
      } else if (!isEdit) {
        // Reset form context for new inspections
        Object.keys(formContext.value).forEach(key => {
          formContext.set(key, '');
        });
        setSelectedTemplate(null);
      }
    }
  }, [show, isEdit, apiData, initialData, editTemplateOptions]);

  // Handle template selection change
  const handleTemplateChange = (templateId: string) => {
    const template = isEdit
      ? (editTemplateOptions.find(t => t.template_id.toString() === templateId)
        || apiData?.available_templates.find(t => t.template_id.toString() === templateId)
        || null)
      : (apiData?.available_templates.find(t => t.template_id.toString() === templateId) || null);

    setSelectedTemplate(template || null);

    // Auto-populate inspection template type with the inspection_types string
    if (template) {
      formContext.set('inspectionTemplateType', template.inspection_types);
    } else {
      formContext.set('inspectionTemplateType', '');
    }
  };

  // Optional-compliance templates (e.g. Delaware/Maryland) expose per-form
  // "Compliance Signature Required" / "Completed Findings Required" toggles, and
  // CCR templates (e.g. Delaware) expose "Use CCR Number" — matching the
  // create-project form. For other templates compliance is forced on by the
  // backend, so no toggle is shown.
  const optionalComplianceTemplates: string[] =
    appContext.get('constants.optionalComplianceTemplates') || [];
  const ccrTemplates: string[] = appContext.get('constants.ccrTemplates') || [];
  const isOptionalComplianceTemplate =
    !!selectedTemplate &&
    optionalComplianceTemplates.includes(selectedTemplate.template_name);
  const isCcrTemplate =
    !!selectedTemplate && ccrTemplates.includes(selectedTemplate.template_name);
  // Completed-findings-required only applies when compliance signatures are on.
  const showFindingCompliance =
    isOptionalComplianceTemplate && !!formContext.value.complianceRequired;

  const handleSubmit = async () => {
    const formData = formContext.value;

    onSubmit({
      ...formData,
      // Only send flags for templates that expose the toggle, coerced to real
      // booleans so an untouched field never sends '' / undefined.
      ...(isOptionalComplianceTemplate
        ? { complianceRequired: !!formData.complianceRequired }
        : {}),
      ...(isCcrTemplate ? { isCcrProject: !!formData.isCcrProject } : {}),
      // For optional-compliance templates, findings-required is only meaningful
      // when compliance is on — force false otherwise. Leave untouched for other
      // templates so their stored value is preserved.
      ...(isOptionalComplianceTemplate
        ? {
          findingComplianceRequired: showFindingCompliance
            ? !!formData.findingComplianceRequired
            : false,
        }
        : {}),
    });
  };

  // Convert API data to dropdown options.
  // For edit mode, use the filtered list from getRoutineEditOptions (prevents duplicate template assignments).
  // For create mode, filter out templates whose inspection_types are already in use.
  const availableTemplates = isEdit
    ? editTemplateOptions
    : (apiData?.available_templates || []).filter(template =>
      !existingInspectionTypes.includes(template.inspection_types)
    );

  const inspectionTemplateNames = availableTemplates.map(template => ({
    value: template.template_id.toString(),
    label: template.template_name,
  }));

  const inspectionIntervals = apiData?.available_intervals.map(interval => ({
    value: interval.interval_id.toString(),
    label: interval.interval_name,
  })) || [];

  const inspectors = [
    { value: 'unassigned', label: 'Unassigned' },
    ...(apiData?.available_inspectors.map(inspector => ({
      value: inspector.user_id.toString(),
      label: inspector.user_name,
    })) || [])
  ];

  const loadFormAddons = async () => {
    if (!junctionId) return;
    const [assignments, available] = await Promise.all([
      billingAddonApi.form.index(junctionId),
      billingAddonApi.form.available(junctionId),
    ]);
    setFormAssignments(assignments);
    setAvailableAddons(available);
  };

  useEffect(() => {
    if (show && junctionId) {
      billingAddonApi.form.hasDivisionAddons(junctionId)
        .then(({ hasDivisionAddons: has }) => setHasDivisionAddons(has));
      loadFormAddons();
    }
    if (!show) {
      setActiveTab('settings');
      setHasDivisionAddons(false);
      setFormAssignments([]);
      setAvailableAddons([]);
      setSelectedAddonId('');
    }
  }, [show, junctionId]);

  const handleAddonAssign = async () => {
    if (!junctionId || !selectedAddonId) return;
    setAddonBusy(true);
    try {
      await billingAddonApi.form.assign(junctionId, Number(selectedAddonId));
      setSelectedAddonId('');
      await loadFormAddons();
      toast.success('Add-on assigned');
    } catch {
      toast.error('Failed to assign add-on');
    } finally {
      setAddonBusy(false);
    }
  };

  const handleAddonRemove = async (assignmentId: number) => {
    if (!junctionId) return;
    setAddonBusy(true);
    try {
      await billingAddonApi.form.remove(junctionId, assignmentId);
      await loadFormAddons();
      toast.success('Add-on removed');
    } catch {
      toast.error('Failed to remove add-on');
    } finally {
      setAddonBusy(false);
    }
  };

  // Track if the interval was changed during edit to signal backend auto-recalculation
  const [intervalChangedDuringEdit, setIntervalChangedDuringEdit] = useState(false);

  // Custom onChange handler for template name and interval
  const handleFormChange = (fieldName: string, value: any) => {
    if (fieldName === 'inspectionTemplateName') {
      handleTemplateChange(value);
    }
    if (fieldName === 'inspectionInterval' && isEdit) {
      // When the interval changes during edit, clear the next inspection date
      // so the backend will auto-recalculate it based on the new interval
      const originalIntervalId = initialData?.inspectionInterval;
      if (value !== originalIntervalId) {
        setIntervalChangedDuringEdit(true);
        formContext.set('nextInspectionDate', '');
      } else {
        setIntervalChangedDuringEdit(false);
        // Restore original date if interval is changed back
        if (initialData?.nextInspectionDate) {
          formContext.set('nextInspectionDate', initialData.nextInspectionDate);
        }
      }
    }
    formContext.set(fieldName, value);
  };

  const fullSchema = {
    inspectionTemplateName: {
      label: 'Inspection Template Name*',
      controlType: UIControlType.select,
      options: inspectionTemplateNames,
      validation: { required: true },
      placeholder: isLoading ? 'Loading templates...' : 'Select inspection template name',
      disabled: isLoading,
    },
    inspectionTemplateType: {
      label: 'Inspection Template Type*',
      controlType: UIControlType.text,
      validation: { required: true },
      placeholder: !selectedTemplate
        ? 'Select template name first'
        : selectedTemplate.inspection_types || 'Auto-populated from selected template',
      disabled: true,
    },
    inspectionInterval: {
      label: 'Inspection Interval*',
      controlType: UIControlType.select,
      options: inspectionIntervals,
      validation: { required: true },
      placeholder: isLoading ? 'Loading intervals...' : 'Select inspection interval',
      disabled: isLoading,
    },
    inspectorName: {
      label: 'Inspector Name*',
      controlType: UIControlType.select,
      options: inspectors,
      validation: { required: true },
      placeholder: isLoading ? 'Loading inspectors...' : 'Select inspector',
      disabled: isLoading,
    },
    nextInspectionDate: {
      label: intervalChangedDuringEdit
        ? 'Next Inspection Date (will be auto-calculated)'
        : 'Next Inspection Date*',
      controlType: UIControlType.date,
      validation: { required: !intervalChangedDuringEdit },
      dateFormat: 'yyyy-MM-dd',
      disabled: intervalChangedDuringEdit,
      placeholder: intervalChangedDuringEdit
        ? 'Auto-calculated based on new interval'
        : '',
    },
  };

  const schema = inspectorOnly
    ? { inspectorName: fullSchema.inspectorName }
    : fullSchema;

  if (!show) return null;

  const showAddonsTab = !!junctionId && hasDivisionAddons;

  return (
    <FormModal
      modalProps={{
        title: isEdit ? 'Modify Inspection' : 'Add New Inspection',
        classes: 'inspection-modal',
      }}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isEdit ? 'Update' : 'Add'}
    >
      <div className="inspection-modal-content">
        {showAddonsTab && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #eee', paddingBottom: 0 }}>
            {(['settings', 'addons'] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '6px 16px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === tab ? '2px solid #1D9E75' : '2px solid transparent',
                  fontWeight: activeTab === tab ? 600 : 400,
                  color: activeTab === tab ? '#1D9E75' : '#555',
                  cursor: 'pointer',
                  fontSize: 13,
                  marginBottom: -2,
                }}
              >
                {tab === 'settings' ? 'Inspection Settings' : 'Billing Add-ons'}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'settings' && (
          isLoading ? (
            <div className="loading-state">
              <p>Loading inspection data...</p>
            </div>
          ) : (
            <>
              <FormSchemaFields
                schema={schema}
                formData={formContext.value}
                onChange={handleFormChange}
              />
              {!inspectorOnly &&
                (isCcrTemplate || isOptionalComplianceTemplate) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      marginTop: 16,
                    }}
                  >
                    {isCcrTemplate && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          cursor: 'pointer',
                          fontSize: 14,
                        }}
                      >
                        <input
                          type='checkbox'
                          checked={!!formContext.value.isCcrProject}
                          onChange={(e) =>
                            formContext.set('isCcrProject', e.target.checked)
                          }
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span>Use CCR Number</span>
                      </label>
                    )}
                    {isOptionalComplianceTemplate && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          cursor: 'pointer',
                          fontSize: 14,
                        }}
                      >
                        <input
                          type='checkbox'
                          checked={!!formContext.value.complianceRequired}
                          onChange={(e) =>
                            formContext.set(
                              'complianceRequired',
                              e.target.checked,
                            )
                          }
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span>Compliance Signature Required</span>
                      </label>
                    )}
                    {showFindingCompliance && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          cursor: 'pointer',
                          fontSize: 14,
                        }}
                      >
                        <input
                          type='checkbox'
                          checked={!!formContext.value.findingComplianceRequired}
                          onChange={(e) =>
                            formContext.set(
                              'findingComplianceRequired',
                              e.target.checked,
                            )
                          }
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span>Completed Findings Required For Compliance</span>
                      </label>
                    )}
                  </div>
                )}
              {intervalChangedDuringEdit && (
                <p style={{ fontSize: '0.85rem', color: '#0d6efd', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  <i className="fa fa-info-circle" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
                  The next inspection date will be automatically recalculated based on the new interval and the last completed inspection.
                </p>
              )}
            </>
          )
        )}

        {activeTab === 'addons' && showAddonsTab && (
          <div style={{ padding: '4px 0' }}>
            {availableAddons.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <select
                  value={selectedAddonId}
                  onChange={e => setSelectedAddonId(e.target.value ? Number(e.target.value) : '')}
                  style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, minWidth: 200 }}
                >
                  <option value="">Select add-on to assign…</option>
                  {availableAddons.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddonAssign}
                  disabled={!selectedAddonId || addonBusy}
                  style={{
                    background: '#1D9E75', color: '#fff', border: 'none',
                    padding: '6px 14px', borderRadius: 4, cursor: selectedAddonId ? 'pointer' : 'not-allowed',
                    fontSize: 13, fontWeight: 600, opacity: selectedAddonId ? 1 : 0.5,
                  }}
                >
                  Assign
                </button>
              </div>
            )}
            {formAssignments.length === 0 ? (
              <p style={{ color: '#999', fontSize: 13 }}>No add-ons assigned to this form.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                    <th style={{ padding: '5px 8px' }}>Name</th>
                    <th style={{ padding: '5px 8px' }}>Date Added</th>
                    <th style={{ padding: '5px 8px' }} />
                  </tr>
                </thead>
                <tbody>
                  {formAssignments.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '5px 8px', fontWeight: 500 }}>{a.billingAddon?.name}</td>
                      <td style={{ padding: '5px 8px', color: '#555' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => handleAddonRemove(a.id)}
                          disabled={addonBusy}
                          style={{
                            background: 'none', border: '1px solid #ccc', padding: '3px 10px',
                            borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#555',
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default InspectionModal;